import * as yup from "yup";
import { Constants } from "../../helpers/Constants";
import { ComplianceOptions } from "./Home/ApplicationDetails.types";

export type AbandonmentFormProps = {
  id: string;
  returnPath: string;
    title: string;
    applicationInspectionId?: number;
    applicationId?: number;

    values?: AbandonmentInput;
  action: "post" | "put";
};


export interface SelectOption {
    id: number;
    name: string;
    description?: string;
}


export interface SoilVerificationData {
    applicationInspectionId: number;
    soilProfiles: SoilProfile[];
    dateCreated: Date;
    createdBy: string;
    dateUpdated?: Date;
    updatedBy?: string;
}


export interface SoilProfileHorizon {
    id?: number;
    soilProfileId?: number;
    horizonNumber: number;
    munsellColor: string;
    colorDescription: string;
    soilTexture: string;
    depthFrom: number;
    depthTo: number;
    dateCreated?: Date;
    createdBy?: string;
    dateUpdated?: Date;
    updatedBy?: string;
}

export interface SoilProfile {
    id?: number;
    applicationInspectionId?: number;
    profileNumber: number;
    horizons: SoilProfileHorizon[];
    usdaSoilSeries: string;
    siteLocation?: string;
    dateCreated?: Date;
    createdBy?: string;
    dateUpdated?: Date;
    updatedBy?: string;
}

export interface SiteEvaluationData {

    propertySizeConformsToSitePlan?: boolean;
    netUsableAreaAvailable?: number;
    totalEstimatedSewageFlow?: number;
    authorizedSewageFlow?: number;
    unobstructedAreaAvailable?: number;
    unobstructedAreaRequired?: number;
    

    benchmarkReferencePointLocation?: string;
    elevationOfProposedSystemSite?: number;
    elevationUnit?: 'Inches' | 'Feet';
    elevationAboveBelow?: 'Above' | 'Below';
    

    surfaceWater?: number;
    ditchesSwales?: number;
    normallyWet?: boolean;
    publicWellsGreaterThan2000GPD?: number;
    publicWellsLessThanEqual2000GPD?: number;
    privateWells?: number;
    nonPotable?: number;
    buildingFoundations?: number;
    propertyLines?: number;
    potableWaterLines?: number;
    

    siteSubjectToFrequentFlooding?: boolean;
    tenYearFlooding?: boolean;
    tenYearFloodEvaluationForSite?: number;
    siteElevation?: number;
    

    observedWaterTable?: number;
    waterTableAboveBelow?: 'Above' | 'Below';
    waterTableType?: 'Apparent' | 'Perched';
    estimatedWetSeasonWaterTableElevation?: number;
    wetSeasonWaterTableAboveBelow?: 'Above' | 'Below';
    highWaterTableVegetation?: boolean;
    redoxFeatures?: boolean;
    redoxFeaturesDepth?: number;
    

    soilReplacementType?: 'No Replacement' | 'FootNote 3' | 'FootNote 4';
    depthToSeverelyLimitedSoil?: number;
    soilTextureForSystemSizing?: string;
    loadingRateForSystemSizing?: number;
    depthOfExcavation?: number;
    drainfieldConfiguration?: 'Bed' | 'Trench' | 'Other';
    drainfieldConfigurationOther?: string;
    drainfieldType?: string;
    bottomOfDrainfield?: number;
    

    remarksAdditionalCriteria?: string;
    siteEvaluatedBy?: string;
    siteEvaluatedDate?: Date | string;
    

    applicationDate?: Date | string;
    applicationCompletedDate?: Date | string;
    readyToIssueDate?: Date | string;
}

export type AbandonmentInput = {
    applicationInspectionId: number
    applicationId: number;
    tankPumpedComplianceId: number;
    tankPumpedComment: string;
    tankPumpedDate: Date | string;
    tankCrushedFilledComplianceId: number;
    tankCrushedFilledComment: string;
    tankCrushedFilledDate: Date | string;
    soilVerificationComplianceId: number;
    soilVerificationComment: string;
    dateCreated: Date | string;
    createdBy: string;
    dateUpdated: Date;
    updatedBy: string;
    inspectionStatusId: number;
    markAll: number;
    tankCrushedFilledCompliance?: ComplianceOptions;
    tankPumpedCompliance?: ComplianceOptions;
    soilVerificationCompliance?: ComplianceOptions;
    
    // New fields for soil profile depths and water table
    soilProfile1Depth?: number;
    soilProfile1DepthBenchmark?: number; // 0 = Select, 1 = Above, 2 = Below
    soilProfile2Depth?: number;
    soilProfile2DepthBenchmark?: number; // 0 = Select, 1 = Above, 2 = Below
    observedWaterTable?: number;
    observedWaterTableBenchmark?: number; // 0 = Select, 1 = Above, 2 = Below
    estimatedWetSeasonWaterTableElevation?: number;
    estimatedWetSeasonWaterTableElevationBenchmark?: number; // 0 = Select, 1 = Above, 2 = Below

    soilProfiles?: SoilProfile[];
};

export type ComplianceData = {
    id: string;
    name: string;
};


export interface SoilProfileEditorProps {
    profile: SoilProfile;
    onSave: (profile: SoilProfile) => void;
    onCancel: () => void;
    isVisible: boolean;
    title: string;
    colorCodes: SelectOption[];
    soilTextures: SelectOption[];
    usdaSoilSeriesOptions: SelectOption[];
}




export const schema = yup.object().shape({

    tankPumpedComplianceId: yup
        .number()
        .min(1, "Tank Pumped Compliance is required")
        .required("Tank Pumped Compliance is required")
        .typeError("Tank Pumped Compliance is required"),

    tankPumpedComment: yup.string().nullable(true)
        .when("tankPumpedComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Pumped Compliance Comment is required")
                .typeError("Tank Pumped ComplianceComment is required"),
        }),
        
    tankPumpedDate: yup.date().nullable(true)
    .when("tankPumpedComplianceId", {
        is: (val: any) => (Number(val) !== Constants.Not_Applicable && Number(val) !== Constants.Unobserved),
        then: yup.date().nullable(false)
        .max(new Date(),"Future Dates are not allowed")
        .required("Tank Pumped Date is required")
        .typeError("Tank Pumped Date is required"),
    })
    .when("tankPumpedComplianceId", {
      is: (val: any) => (Number(val) === Constants.Not_Applicable || Number(val) === Constants.Unobserved),
      then: yup.date() .nullable(true).transform((value) => isNaN(value?.getTime()) ? null : value).notRequired()
    }),

    tankCrushedFilledComplianceId: yup
        .number()
        .min(1, "Tank Crushed Filled Compliance is required")
        .required("Tank Crushed Filled Compliance is required")
        .typeError("Tank Crushed Filled Compliance is required"),

    tankCrushedFilledComment: yup.string().nullable(true)
        .when("tankCrushedFilledComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Crushed Filled Compliance Comment is required")
                .typeError("Tank Crushed Filled ComplianceComment is required"),
        }),

    tankCrushedFilledDate: yup.date().nullable(true)
    .when("tankCrushedFilledComplianceId", {
        is: (val: any) => (Number(val) !== Constants.Not_Applicable && Number(val) !== Constants.Unobserved),
        then: yup.date().nullable(false)
        .max(new Date(), "Future Dates are not allowed")
        .required("Tank Crushed Filled Date is required")
        .typeError("Tank Crushed Filled Date is required")
    })
    .when("tankCrushedFilledComplianceId", {
      is: (val: any) => (Number(val) === Constants.Not_Applicable || Number(val) === Constants.Unobserved),
      then: yup.date() .nullable(true).transform((value) => isNaN(value?.getTime()) ? null : value).notRequired()
    }),

    soilVerificationComplianceId: yup
        .number()
        .min(1, "Soil Verification Compliance is required")
        .required("Soil Verification Compliance is required")
        .typeError("Soil Verification Compliance is required"),

    soilVerificationComment: yup.string().nullable(true)
        .when("soilVerificationComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Explanation of Compliance Violation is required")
                .typeError("Explanation of Compliance Violation is required"),
        }),


    soilProfiles: yup.array().nullable(true)
        .when("soilVerificationComplianceId", {
            is: (complianceId: any) => {
                return complianceId === Constants.Out_of_Compliance;
            },
            then: yup.array().of(
                yup.object().shape({
                    profileNumber: yup.number().required(),
                    horizons: yup.array().of(
                        yup.object().shape({
                            munsellColor: yup.string().required("Munsell Color is required"),
                            soilTexture: yup.string().required("Soil Texture is required"),
                            depthFrom: yup.number()
                                .min(0, "Depth From must be positive")
                                .required("Depth From is required")
                                .test('depth-range', 'Depth To must be greater than Depth From', function(value) {
                                    const { depthTo } = this.parent;
                                    return value !== undefined && depthTo !== undefined && depthTo > value;
                                }),
                            depthTo: yup.number()
                                .min(0, "Depth To must be positive")
                                .required("Depth To is required")
                        })
                    ).min(1, "At least one soil horizon is required"),

                    usdaSoilSeries: yup.string().required("USDA Soil Series is required")
                })
            ).min(1, "At least one soil profile is required").required("Soil profiles are required when out of compliance"),
            otherwise: yup.array().nullable()
        }),





    // New field validations
    soilProfile1Depth: yup.number().nullable(true)
        .when("soilVerificationComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.number()
                .min(0, "Soil Profile 1 Depth must be positive")
                .required("Soil Profile 1 Depth is required")
                .typeError("Soil Profile 1 Depth is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    soilProfile1DepthBenchmark: yup.number().nullable(true)
        .when("soilVerificationComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.number()
                .min(1, "Soil Profile 1 Depth Benchmark is required")
                .max(2, "Invalid benchmark selection")
                .required("Soil Profile 1 Depth Benchmark is required")
                .typeError("Soil Profile 1 Depth Benchmark is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    soilProfile2Depth: yup.number().nullable(true)
        .when("soilVerificationComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.number()
                .min(0, "Soil Profile 2 Depth must be positive")
                .required("Soil Profile 2 Depth is required")
                .typeError("Soil Profile 2 Depth is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    soilProfile2DepthBenchmark: yup.number().nullable(true)
        .when("soilVerificationComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.number()
                .min(1, "Soil Profile 2 Depth Benchmark is required")
                .max(2, "Invalid benchmark selection")
                .required("Soil Profile 2 Depth Benchmark is required")
                .typeError("Soil Profile 2 Depth Benchmark is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

        observedWaterTable: yup.number().nullable(true)
            .when("soilVerificationComplianceId", {
                is: (val: any) => val === Constants.Out_of_Compliance,
                then: yup.number()
                    .min(0, "Observed Water Table must be positive")
                    .required("Observed Water Table is required")
                    .typeError("Observed Water Table is required"),
                otherwise: yup.number().nullable().notRequired()
            }),

        observedWaterTableBenchmark: yup.number().nullable(true)
            .when("soilVerificationComplianceId", {
                is: (val: any) => val === Constants.Out_of_Compliance,
                then: yup.number()
                    .min(1, "Observed Water Table Benchmark is required")
                    .max(2, "Invalid benchmark selection")
                    .required("Observed Water Table Benchmark is required")
                    .typeError("Observed Water Table Benchmark is required"),
                otherwise: yup.number().nullable().notRequired()
            }),

        estimatedWetSeasonWaterTableElevation: yup.number().nullable(true)
            .when("soilVerificationComplianceId", {
                is: (val: any) => val === Constants.Out_of_Compliance,
                then: yup.number()
                    .min(0, "Estimated Wet Season Water Table Elevation must be positive")
                    .required("Estimated Wet Season Water Table Elevation is required")
                    .typeError("Estimated Wet Season Water Table Elevation is required"),
                otherwise: yup.number().nullable().notRequired()
            }),

        estimatedWetSeasonWaterTableElevationBenchmark: yup.number().nullable(true)
            .when("soilVerificationComplianceId", {
                is: (val: any) => val === Constants.Out_of_Compliance,
                then: yup.number()
                    .min(1, "Estimated Wet Season Water Table Elevation Benchmark is required")
                    .max(2, "Invalid benchmark selection")
                    .required("Estimated Wet Season Water Table Elevation Benchmark is required")
                    .typeError("Estimated Wet Season Water Table Elevation Benchmark is required"),
                otherwise: yup.number().nullable().notRequired()
            }),

});